/** Central declarations for automatic page notices. Explicit button actions are not page rules. */
import type { PageState } from './page-state'

export type PageFacts = {
  // No automatic notice reads a study-builder fact today (the picker is always visible, so the rule list is empty).
  // A future notice adds exactly the facts it needs here; the panel must not derive facts the rules never read.
  'study-builder': Record<string, never>
}
export type PageName = keyof PageFacts
export type PageNotice = { readonly id: 'choose-product'; readonly display: 'modal' }
type Rule<Facts> = PageNotice & { readonly when: (facts: Facts) => boolean }

/** Declaration order is priority. Other pages are the explicit #4264 rollout. */
export const pageRules: { readonly [Page in PageName]: readonly Rule<PageFacts[Page]>[] } = {
  // Off: Choose product is always visible, so the page never opens the picker itself.
  // Do not delete this list or the rule system; add the next automatic notice here.
  'study-builder': [],
}

export type PresentedNotices = { readonly visit: string; readonly ids: readonly PageNotice['id'][] }

/** Pure decision: not loaded and failed are never treated as empty. At most one decision is returned. */
export function evaluatePageNotice<Page extends PageName>(input: {
  page: Page
  state: PageState
  visit: string
  facts: PageFacts[Page]
  presented: PresentedNotices | null
}): PageNotice | null {
  if (input.state.status !== 'ready') return null
  const seen = input.presented?.visit === input.visit ? input.presented.ids : []
  const rule = pageRules[input.page].find((candidate) => !seen.includes(candidate.id) && candidate.when(input.facts))
  return rule ? { id: rule.id, display: rule.display } : null
}

/** Presentation is consumed once per source visit, including React effect replays. No storage or clocks. */
export function recordPageNotice(
  presented: PresentedNotices | null,
  visit: string,
  notice: PageNotice,
): PresentedNotices {
  const ids = presented?.visit === visit ? presented.ids : []
  return { visit, ids: ids.includes(notice.id) ? ids : [...ids, notice.id] }
}
